/**
 * Header Component - Reutilizável em todas as páginas
 * Features:
 * - Logo clicável (navega para home)
 * - Botão "Back to Home" (condicional via showBackButton)
 * - Botão Logout (visível apenas quando autenticado)
 * - Sticky header com backdrop blur
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logout, clearJustLoggedInFlag } from "@/store/slices/authSlice";
import { fetchTeacherProfile } from "@/store/slices/teacherProfileSlice";
import { selectTeacherProfile } from "@/store/selectors/teacherProfileSelectors";
import { Button } from "@/components/ui/button";
import { UserDropdown } from "./UserDropdown";
import logo from "@/assets/logo.png";
import { logger } from "@/utils/logger";

interface HeaderProps {
  /**
   * Se true, mostra botão "Back to Home" (útil em páginas como Login)
   * @default false
   */
  showBackButton?: boolean;

  /**
   * Se true, mostra botão Logout quando usuário está autenticado
   * @default true
   */
  showLogout?: boolean;

  /**
   * Se true, usa fundo sólido com gradient (para páginas com scroll)
   * Se false, fica transparente (para Hero sections)
   * @default true
   */
  solidBackground?: boolean;
}

export function Header({
  showBackButton = false,
  showLogout = true,
  solidBackground = true,
}: HeaderProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const { isAuthenticated, user } = auth;
  const profile = useAppSelector(selectTeacherProfile);

  // Debug user state
  useEffect(() => {
    if (user) {
      logger.log("👤 [Header] User state:", {
        id: user.id,
        email: user.email,
        is_admin: user.is_admin,
        role: user.role,
      });
    }
  }, [user]);

  // Fetch teacher profile when authenticated (force refresh if just logged in)
  useEffect(() => {
    logger.log("🎯 [Header] useEffect triggered", {
      isAuthenticated,
      hasProfile: !!profile,
      justLoggedIn: auth.justLoggedIn,
    });

    if (!isAuthenticated) {
      logger.log("⏭️ [Header] Skipping fetch", { reason: "not authenticated" });
      return;
    }

    // ⚠️ MODIFICADO: Buscar se não tem perfil OU se acabou de fazer login
    if (!profile || auth.justLoggedIn) {
      const reason = !profile ? "no profile" : "just logged in";
      logger.log("📥 [Header] Dispatching fetchTeacherProfile", { reason });

      dispatch(fetchTeacherProfile(true)); // forceRefresh = true

      // Limpar flag após dispatch
      if (auth.justLoggedIn) {
        dispatch(clearJustLoggedInFlag());
      }
      return;
    }

    logger.log("⏭️ [Header] Skipping fetch", { reason: "already has profile" });
  }, [dispatch, isAuthenticated, profile, auth.justLoggedIn]);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/login");
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  // Classes dinâmicas baseadas em solidBackground
  const headerClasses = solidBackground
    ? "fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-primary/95 to-accent/95 backdrop-blur-sm shadow-lg"
    : "fixed top-0 left-0 right-0 z-50 animate-fade-in";

  return (
    <header className={headerClasses}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo - esquerda */}
          <div className="flex items-center">
            <img
              src={logo}
              alt="Simu Fluent Logo"
              className="h-10 w-auto cursor-pointer"
              onClick={handleLogoClick}
            />
          </div>

          {/* Navigation Links - centro */}
          <nav className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex items-center gap-6">
            <Button
              variant="ghost"
              className="text-white hover:text-white hover:bg-white/20"
              onClick={() => navigate("/for-businesses")}
            >
              For Businesses
            </Button>
          </nav>

          {/* Botão Login/Logout - direita */}
          {/* {showBackButton && (
            <Button
              variant="outline"
              className="bg-white/10 backdrop-blur-sm text-white border-white/20 hover:bg-white/20 hover:text-white"
              onClick={handleBackToHome}
            >
              ← Back to Home
            </Button>
          )} */}

          {showLogout && !isAuthenticated && (
            <Button
              variant="outline"
              className="bg-white/10 backdrop-blur-sm text-white border-white/20 hover:bg-white/20 hover:text-white"
              onClick={() => navigate("/login")}
            >
              Login
            </Button>
          )}

          {showLogout && isAuthenticated && (
            <UserDropdown
              userName={profile?.name || user?.name || user?.display || "User"}
              userEmail={profile?.email || user?.email}
            />
          )}
        </div>
      </div>
    </header>
  );
}
